import React, { useState } from 'react';
import { Eye, EyeOff, User, Loader2 } from 'lucide-react';
import FormInput from '../../FormInput';

const UtilisateurForm = ({
  formData,
  formErrors,
  onInputChange,
  onSubmit,
  submitting,
  selectedUtilisateur,
  onCancel,
  roles = []
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    onInputChange({
      target: {
        name: 'image',
        value: file
      }
    });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Photo de profil */}
      <div className="flex items-center space-x-4">
        <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
          {formData.image ? (
            <img
              src={typeof formData.image === 'string' ? formData.image : URL.createObjectURL(formData.image)}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <User className="h-8 w-8 text-gray-400" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Photo de profil
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-[#202654] hover:file:bg-blue-100"
          />
        </div>
      </div>

      {/* Informations personnelles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={onInputChange}
          error={formErrors.email}
          required
        />

        <FormInput
          label="Téléphone"
          name="telephone"
          type="tel"
          value={formData.telephone}
          onChange={onInputChange}
          error={formErrors.telephone}
        />

        <FormInput
          label="Prénom"
          name="first_name"
          type="text"
          value={formData.first_name}
          onChange={onInputChange}
          error={formErrors.first_name}
        />

        <FormInput
          label="Nom"
          name="last_name"
          type="text"
          value={formData.last_name}
          onChange={onInputChange}
          error={formErrors.last_name}
        />
      </div>

      {/* Sélection du rôle */}
      <div className="space-y-4">
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
            Rôle
          </label>
          <select
            id="role"
            name="role"
            value={formData.role || ''}
            onChange={onInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
          >
            <option value="">Sélectionner un rôle</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          {formErrors.role && (
            <p className="mt-1 text-sm text-red-600">{formErrors.role}</p>
          )}
        </div>
      </div>

      {/* Mots de passe pour nouveau utilisateur */}
      {!selectedUtilisateur && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FormInput
              label="Mot de passe"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={onInputChange}
              error={formErrors.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <div className="relative">
            <FormInput
              label="Confirmer le mot de passe"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={onInputChange}
              error={formErrors.confirmPassword}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
      )}

      {/* Options */}
      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={onInputChange}
            className="h-4 w-4 text-[#202654] focus:ring-[#202654] border-gray-300 rounded"
          />
          <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
            Compte actif
          </label>
        </div>

        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Permissions</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Administrateur</label>
                <p className="text-xs text-gray-500">Accès complet à toutes les fonctionnalités</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_superuser"
                  checked={formData.is_superuser}
                  onChange={onInputChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#202654]"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900">Staff</label>
                <p className="text-xs text-gray-500">Accès à l'interface d'administration</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_staff"
                  checked={formData.is_staff}
                  onChange={onInputChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#202654]"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#1a1f45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
              Enregistrement...
            </>
          ) : (
            selectedUtilisateur ? 'Modifier' : 'Ajouter'
          )}
        </button>
      </div>
    </form>
  );
};

export default UtilisateurForm; 